import queryString from 'query-string';
import * as luxon from 'luxon';

import {
  IonApp,
  IonContent,
  setupIonicReact,
} from '@ionic/react';

import { useCallback, useEffect, useState } from 'react';

import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import i18next from 'i18next';
import jwt_decode from 'jwt-decode';
import _ from 'lodash';

import { Modules } from '@flexo/templates';

import './app.module.scss';
import '../assets/fonts.scss';
import '../assets/typography.scss';
import '../assets/colors.scss';
// eslint-disable-next-line
import { default as COLORS } from '../assets/colors';
import '@ionic/react/css/core.css';

import { default as MODULES } from './app.modules';
import { LeftDrawer, RightDrawer } from '@flexo/organisms';
import { FirebaseService, IconWrapper, auth,  } from '@flexo/general';
import { useDispatch, useSelector } from 'react-redux';
import { CALENDAR, THEME, USER } from '@flexo/reducers';
import { _navigate, LoadingDots } from '@flexo/atoms';

import { useTranslation } from 'react-i18next';

const firebaseService = new FirebaseService()

const ENV = (import.meta as any).env;

export interface ILoginFlow {
  logInState: 'LOGIN' | 'REGISTER' | 'ACCOUNT_CREATION' | 'FORGOT_PASSWORD';
}

setupIonicReact(); //initiates ionic with react

function App() {

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const user = useSelector((state: any) => state.user);
  const calendar = useSelector((state: any) => state.calendar);

  const { selectedDate } = calendar.hiveCalendar;

  const [showSplashScreen, setShowSplashScreen] = useState(true);
  const [availableModules, setAvailableModules] = useState([]);
  // Remove dots counter state as it's now handled by the LoadingDots component

  const location = window.location;
  const browserLanguage = navigator.language.split('-')[0] || null;

  const changeLanguage = useCallback(async () => {


    await i18next.changeLanguage(
      `${ENV.VITE_APP_ORG}_${browserLanguage || ENV.VITE_APP_LANGUAGE}`
    );
    dispatch( THEME.SetColors({colors: COLORS} as any));
  }, []);

  useEffect(() => {
    const unsubscribe = firebaseService.onAuthStateChange((user) => {

      setShowSplashScreen(true);

      if (user) {
        let tokenContent: any = {};

        const pathName = location.pathname
        const pathNameSplit = location.pathname.split('/')[1];
        const searchParams = location.search;

        try {
          const accessToken = user?.user?.accessToken || 
                              user?.stsTokenManager?.accessToken || 
                              (user as any)?._tokenResponse?.idToken;
                              
          if (accessToken) {
            tokenContent = jwt_decode(accessToken);
          }
        } catch (error: any) {
          console.warn("Token decode failed in auth state change:", error.message);
        }

        dispatch(USER.SetUserAsync({ ...user, tokenContent }) as any)

        if (MODULES.find((module: any) => module.name === pathNameSplit)?.needsAuth === true) {
          setTimeout(() => {
            _navigate(`${pathName}${searchParams}`)
          }, 1000)
        }

      }

      setTimeout( () => setShowSplashScreen(false), 1000 )
      
    });

    // Clean up the listener when the component is unmounted
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    let _modules: any = MODULES.filter((module: any) => module.active?.default === true);
    _modules = _modules.filter((module: any) => module?.needsAuth === ( (user?.tokenContent || user?._tokenResponse) ? true : false ))
    if (!_.isEqual(_modules, availableModules) ) {
      setAvailableModules(_modules)
    }
  }, [ user?.tokenContent, user?._tokenResponse ])


  useEffect(() => {

    (async () => {
      if (user?.logOut === true) {
        await firebaseService.signOut();
        if ( localStorage.getItem('redirectToForgotPassword') === 'true' ) {
          localStorage.removeItem('redirectToForgotPassword');
          _navigate('/forgot-password');
        } else {
          _navigate('/login');
        }
        dispatch(USER.ResetUserAsync() as any)
        
        setShowSplashScreen(false);
      }
    })()

  }, [user?.logOut])

  // Remove the useEffect for dots counter animation as it's now handled by the LoadingDots component
  useEffect(() => {
    if (user?.splashScreen === true) {
      setShowSplashScreen(true);
    }
  }, [user?.splashScreen])
  

  useEffect(() => {
    const params = queryString.parse(window.location.search);
    const hasKeys = (obj, ...keys) => keys.every(key => key in obj);
    if (Object.keys(params).length > 0 && hasKeys(params, 'dateTo', 'dateFrom', 'timeSet') === true) {
      
      const _startDate = new Date(params.dateFrom as string);
      const _endDate = new Date(params.dateTo as string);

      if ((`${_startDate} ${_endDate}`).toLowerCase()?.includes('invalid')) { return; }

      if (
        (luxon.DateTime.fromJSDate(_startDate).toMillis() === luxon.DateTime.fromJSDate(selectedDate[0]).toMillis()) &&
        (luxon.DateTime.fromJSDate(_endDate).toMillis() === luxon.DateTime.fromJSDate(selectedDate[1]).toMillis())
      ) {
        return;
      }


      dispatch(CALENDAR.setHiveDate({
        timeSet: params.timeSet,
        date: [ _startDate, _endDate ],
        setQueryParams: false,
      } as any))
    }
  }, [])

  useEffect(() => {
    // Handle token expiration (401 Unauthorized) events
    const handleTokenExpired = async () => {
      // Log user out if token is expired
      dispatch(USER.SetUserAsync({ ...user, logOut: true }) as any);
    };
    
    // Add event listener for token expiration
    window.addEventListener('firebase-token-expired', handleTokenExpired);
    
    // Clean up the listener when the component unmounts
    return () => {
      window.removeEventListener('firebase-token-expired', handleTokenExpired);
    };
  }, [user, dispatch]);


  



  return (

    showSplashScreen 
      ? <div
        className="flex flex-align-center flex-justify-center flex-column"
        style={{ height: '100vh', width: '100vw' }}
      >
        <IconWrapper
          iconName={ENV?.VITE_APP_IS_WEB === 'true' ? 'logo_wide' : 'logo'}
        />
        {
          user?.isLoggingOut === true && 
          <LoadingDots />
        }
      </div>
      : 



        <IonApp className={`Web__APP`}>

          

            {
              <IonContent scrollY={false} className='IonScroll'>

                  <LeftDrawer />
                <BrowserRouter>
                  
                    {
                      user?.SetUserAsyncStatus !== 'PENDING' &&
                      <Routes>

                        {
                          availableModules
                            .map((module: any) => {
                              return <Route key={module.name} path={module?.needsID ? `${module.name}/:id` : module.name} element={<Modules MODULE={(module)} COLORS={COLORS} />} />

                            })
                          }
                          
                          {
                            (auth?.currentUser)
                              ? < Route path='*' element={ <Navigate to={ `/${ENV.VITE_APP_FIRST_PAGE}`} />} />
                              : <>
                                {/* Allow direct access to forgot-password-mobile without redirecting */}
                                <Route 
                                  path="/forgot-password" 
                                  element={
                                    <Modules 
                                      MODULE={(MODULES.find(m => m.name === 'forgot-password'))} 
                                      COLORS={COLORS}
                                    />
                                  } 
                                />
                                {/* All other unauthenticated routes redirect based on conditions */}
                                <Route path='*' element={
                                  (() => {
                                    // Check if there's a flag to redirect to forgot password
                                    // const redirectToForgotPassword = localStorage.getItem('redirectToForgotPassword') === 'true';
                                    
                                    // // If flag exists, clear it and redirect to forgot password
                                    // if (redirectToForgotPassword) {
                                    //   localStorage.removeItem('redirectToForgotPassword');
                                    //   return <Navigate to="/forgot-password" />;
                                    // }
                                    
                                    // Otherwise redirect to login as usual
                                    return <Navigate to={`/login`} />;
                                  })()
                                } />
                              </>
                          }


                      </Routes>
                    }
                    <RightDrawer />
                  </BrowserRouter>



              </IonContent>
            }


        </IonApp>

  );
}

export default App;